export type AuthInvalidation = 'expired' | 'signed-out'

type Listener = (reason: AuthInvalidation) => void

let controller = new AbortController()
let generation = 0
const listeners = new Set<Listener>()
const beforeReset = new Set<() => void>()
const guestListeners = new Set<() => void>()

const channel: BroadcastChannel | null =
  typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('grovus-auth')
channel?.addEventListener('message', (event) => {
  if (event.data === 'signed-out') invalidate('signed-out')
  if (event.data === 'guest-changed') for (const listener of guestListeners) listener()
})

/**
 * Aborts everything the current session has in flight and moves the generation on, so a
 * response that was already on its way is discarded rather than applied. It says nothing
 * about whether the session is still valid: the boundary uses it when it is about to
 * revalidate, `invalidate` builds on it when the session is known to be gone.
 */
export function resetAuthWork() {
  for (const callback of beforeReset) {
    try {
      callback()
    } catch {
      // One callback failing must not keep the session's requests alive.
    }
  }
  generation += 1
  controller.abort(new DOMException('auth session invalidated', 'AbortError'))
  controller = new AbortController()
}

function invalidate(reason: AuthInvalidation) {
  resetAuthWork()
  for (const listener of listeners) listener(reason)
}

/** Aborts when the session is invalidated; combine with a request's own signal via `AbortSignal.any`. */
export function authSignal(): AbortSignal {
  return controller.signal
}

/**
 * Runs `callback` synchronously at the start of every `resetAuthWork()`, before anything is
 * aborted: on `pagehide`, sign-out and expiry alike. The add-client hook marks the keys it
 * has in flight here (GRO-82 design, D3), because a `pagehide` listener of its own would be
 * removed by the boundary's synchronous unmount before it ran.
 */
export function onBeforeAuthReset(callback: () => void): () => void {
  beforeReset.add(callback)
  return () => beforeReset.delete(callback)
}

export function authGeneration(): number {
  return generation
}

/** AuthBoundary calls this to react to expiry, sign-out and other tabs' sign-out broadcasts alike. */
export function subscribeAuth(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** The API client calls this when any authenticated request answers 401. */
export function reportSessionExpired() {
  invalidate('expired')
}

/** Called once sign-out is confirmed (204 or 401); broadcasts to other tabs with no token/user payload. */
export function reportSignedOut() {
  invalidate('signed-out')
  channel?.postMessage('signed-out')
}

/**
 * Tells the other tabs that this browser's guest grant changed: a link was opened, or the
 * guest left (GRO-83 design, "One browser, one guest grant"). The message carries nothing;
 * a guest-mode tab re-reads the session to learn what changed.
 */
export function reportGuestChanged() {
  channel?.postMessage('guest-changed')
}

/** A guest-mode tab listens here for another tab's `reportGuestChanged()`. */
export function subscribeGuestChanged(listener: () => void): () => void {
  guestListeners.add(listener)
  return () => guestListeners.delete(listener)
}
